import React, { useRef, useEffect, useCallback } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-sprite-thumbnails";
import clsx from "clsx";

import {
  cacheVideo,
  clearVideoCache,
  getCacheStatus,
  onCacheProgress,
  toAbsoluteUrl,
  CacheProgress,
} from "../../libs/videoCache";

import "./VideoCacheButton.scss";

export interface IVideoJSProps {
  className?: string;
  sourceUrl: string;
  options?: videojs.PlayerOptions;
  playing?: boolean;
  showVideo: boolean;
  onReady?: (player: videojs.Player) => void;
  onFirstVideoPlay?: (player: videojs.Player) => void;
  onVideoStart?: (player: videojs.Player) => void;
  onVideoEnd?: (player: videojs.Player) => void;
}

const SPRITE_THUMBNAIL_WIDTH = 160;
const SPRITE_THUMBNAIL_HEIGHT = 90;
const SPRITE_THUMBNAIL_COLUMNS = 10;
const SPRITE_THUMBNAIL_ROWS = 10;
const SPRITE_THUMBNAIL_INTERVAL_SEC = 5;
const SPRITE_FILENAME_INDEX_DIGITS = 9;

const buildSpriteThumbnailsConfig = (sourceUrl: string) => {
  const parentDir = sourceUrl.slice(0, sourceUrl.lastIndexOf("/"));
  return {
    url: `${parentDir}/Thumbnail_{index}.jpg`,
    width: SPRITE_THUMBNAIL_WIDTH,
    height: SPRITE_THUMBNAIL_HEIGHT,
    columns: SPRITE_THUMBNAIL_COLUMNS,
    rows: SPRITE_THUMBNAIL_ROWS,
    interval: SPRITE_THUMBNAIL_INTERVAL_SEC,
    idxTag: (i: number) =>
      String(i + 1).padStart(SPRITE_FILENAME_INDEX_DIGITS, "0"),
  };
};

const buildSource = (sourceUrl: string) => ({
  src: sourceUrl + ".m3u8",
  type: "application/x-mpegURL",
  spriteThumbnails: buildSpriteThumbnailsConfig(sourceUrl),
});

const enableSpriteThumbnails = (player: videojs.Player) => {
  if (typeof player.spriteThumbnails === "function") {
    player.spriteThumbnails({});
  }
};

const defaultOptions: (
  sourceUrl: string,
) => videojs.PlayerOptions = sourceUrl => ({
  autoplay: false,
  controls: true,
  responsive: true,
  fill: true,
  preload: "metadata",
  sources: [buildSource(sourceUrl)],
  poster: sourceUrl + ".jpg",
});

export const VideoJS: React.FC<IVideoJSProps> = ({
  className = "",
  sourceUrl,
  options,
  playing,
  showVideo,
  onReady,
  onFirstVideoPlay,
  onVideoStart,
  onVideoEnd,
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef<videojs.Player | null>(null);
  const cacheStateRef = useRef<"idle" | "caching" | "done" | "error">("idle");
  const cacheButtonRef = useRef<HTMLButtonElement | null>(null);
  const sourceUrlRef = useRef(sourceUrl);

  useEffect(() => {
    sourceUrlRef.current = sourceUrl;
  }, [sourceUrl]);

  const updateCacheButtonUI = useCallback(
    (state: string, progress?: number) => {
      const btn = cacheButtonRef.current;
      if (!btn) return;
      btn.classList.remove(
        "vjs-cache-idle",
        "vjs-cache-caching",
        "vjs-cache-done",
        "vjs-cache-error",
      );
      btn.classList.add(`vjs-cache-${state}`);
      if (state === "caching" && progress !== undefined) {
        btn.textContent = `${progress}%`;
        btn.dataset.tooltip = `一時保存中... ${progress}%`;
      } else if (state === "done") {
        btn.textContent = "";
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + 14);
        const expiryStr = `${expiry.getMonth() + 1}/${expiry.getDate()}`;
        btn.dataset.tooltip = `一時保存済み（${expiryStr}まで有効・クリックで削除）`;
      } else if (state === "error") {
        btn.textContent = "";
        btn.dataset.tooltip = "保存失敗（クリックで再試行）";
      } else {
        btn.textContent = "";
        btn.dataset.tooltip =
          "動画を一時保存します。保存後は通信環境に関係なくスムーズに再生できます";
      }
    },
    [],
  );

  const handleCacheToggle = useCallback(async () => {
    const state = cacheStateRef.current;
    if (state === "caching") return;

    const currentUrl = sourceUrlRef.current + ".m3u8";

    if (state === "done") {
      await clearVideoCache(currentUrl);
      cacheStateRef.current = "idle";
      updateCacheButtonUI("idle");
      return;
    }

    cacheStateRef.current = "caching";
    updateCacheButtonUI("caching", 0);

    try {
      await cacheVideo(currentUrl);
    } catch (e) {
      console.error("[VideoCache] キャッシュ失敗:", e);
      cacheStateRef.current = "error";
      updateCacheButtonUI("error");
    }
  }, [updateCacheButtonUI]);

  useEffect(() => {
    const videoUrl = toAbsoluteUrl(sourceUrl + ".m3u8");
    const unsubscribe = onCacheProgress((progress: CacheProgress) => {
      if (progress.videoUrl !== videoUrl) return;
      if (progress.phase === "segments" && progress.totalSegments > 0) {
        const pct = Math.round(
          (progress.segmentsCached / progress.totalSegments) * 100,
        );
        updateCacheButtonUI("caching", pct);
      } else if (progress.phase === "done") {
        cacheStateRef.current = "done";
        updateCacheButtonUI("done");
      } else if (progress.phase === "error") {
        cacheStateRef.current = "error";
        updateCacheButtonUI("error");
      }
    });
    return unsubscribe;
  }, [sourceUrl, updateCacheButtonUI]);

  useEffect(() => {
    cacheStateRef.current = "idle";
    updateCacheButtonUI("idle");
    let cancelled = false;
    getCacheStatus(sourceUrl + ".m3u8").then(status => {
      if (cancelled) return;
      if (status.cached) {
        cacheStateRef.current = "done";
        updateCacheButtonUI("done");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sourceUrl, updateCacheButtonUI]);

  const addCacheButton = useCallback(
    (playerEl: Element) => {
      if (playerEl.querySelector(".vjs-cache-button")) return;
      const controlBarEl = playerEl.querySelector(
        ".vjs-control-bar",
      ) as HTMLElement | null;
      if (!controlBarEl) return;

      const btn = document.createElement("button");
      btn.className = "vjs-cache-button vjs-control vjs-button vjs-cache-idle";
      btn.dataset.tooltip =
        "動画を一時保存します。保存後は通信環境に関係なくスムーズに再生できます";
      btn.setAttribute("type", "button");
      btn.addEventListener("click", handleCacheToggle);
      cacheButtonRef.current = btn;

      const fullscreenBtn = controlBarEl.querySelector(
        ".vjs-fullscreen-control",
      );
      if (fullscreenBtn) {
        controlBarEl.insertBefore(btn, fullscreenBtn);
      } else {
        controlBarEl.appendChild(btn);
      }

      if (cacheStateRef.current !== "idle") {
        updateCacheButtonUI(cacheStateRef.current);
      }
    },
    [handleCacheToggle, updateCacheButtonUI],
  );

  React.useEffect(() => {
    if (!playerRef.current) {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const player = (playerRef.current = videojs(
        videoElement,
        options ?? defaultOptions(sourceUrl),
        () => {
          onReady && onReady(player);
          addCacheButton(player.el());
          enableSpriteThumbnails(player);
        },
      ));
    }
  }, [options, videoRef, sourceUrl, onReady, addCacheButton]);

  React.useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    player.src(buildSource(sourceUrl));
    player.poster(sourceUrl + ".jpg");

    const firstVideoPlayHandler = () => {
      onFirstVideoPlay?.(player);
    };
    const videoStartHandler = () => {
      onVideoStart?.(player);
    };
    const videoEndHandler = () => {
      onVideoEnd?.(player);
    };

    onFirstVideoPlay && player.one("play", firstVideoPlayHandler);
    onVideoStart && player.on("play", videoStartHandler);
    onVideoEnd && player.on("ended", videoEndHandler);

    return () => {
      onFirstVideoPlay && player.off("play", firstVideoPlayHandler);
      onVideoStart && player.off("play", videoStartHandler);
      onVideoEnd && player.off("ended", videoEndHandler);
    };
  }, [sourceUrl, onFirstVideoPlay, onVideoStart, onVideoEnd]);

  React.useEffect(() => {
    if (playerRef.current) {
      playing && showVideo
        ? playerRef.current.play()
        : playerRef.current.pause();
    }
  }, [playerRef, playing, showVideo]);

  React.useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <div data-vjs-player>
      <video
        ref={videoRef}
        className={clsx("video-js vjs-big-play-centered", className)}
      />
    </div>
  );
};

export default VideoJS;
